import EventEmitter from 'events';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ArcwareInit, type ArcwarePixelStreaming } from '@arcware-cloud/pixelstreaming-websdk';

import {
  PixelStreamingContext,
  type PixelStreamingStatus,
} from 'src/features/PixelStreaming/contexts/PixelStreamingContext';

import styles from './ArcwareContextProvider.module.scss';

export interface IArcwareContext {
  status: PixelStreamingStatus;
}

export const ArcwareContext = createContext<IArcwareContext>({
  status: 'unknown',
});

type Props = {
  shareId: string;
  logo?: string;
  children: React.ReactNode;
};

export default function ArcwareContextProvider({ shareId, logo, children }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pixelStreamingRef = useRef<ArcwarePixelStreaming | null>(null);
  const [descriptor] = useState(() => new EventEmitter());
  const videoInitializedRef = useRef(false);
  const flushedRef = useRef(false);
  const preOpenMessagesRef = useRef<any[]>([]);
  const [status, setStatus] = useState<PixelStreamingStatus>('unknown');

  const flushPreOpenMessages = useCallback(() => {
    const pixelStreaming = pixelStreamingRef.current;
    if (!pixelStreaming || !videoInitializedRef.current) return;

    const queued = preOpenMessagesRef.current;
    preOpenMessagesRef.current = [];

    // Guarantee the `initial` packet is delivered before anything else so
    // Unreal can process it ahead of `join_session` and subsequent messages.
    const ordered = [
      ...queued.filter((message) => message?.type === 'initial'),
      ...queued.filter((message) => message?.type !== 'initial'),
    ];
    ordered.forEach((message) => {
      pixelStreaming.emitUIInteraction(message);
      console.debug('emit emitUIInteraction (flush)', message);
    });

    flushedRef.current = true;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !shareId) return;

    const { PixelStreaming, Application } = ArcwareInit(
      { shareId },
      {
        initialSettings: {
          AutoConnect: true,
          AutoPlayVideo: true,
          StartVideoMuted: false,
          HoveringMouse: true,
          KeyboardInput: true,
          MouseInput: true,
          TouchInput: true,
        },
        settings: {
          fullscreenButton: false,
          settingsButton: false,
          infoButton: false,
          audioButton: false,
          micButton: false,
          stopButton: false,
          connectionStrengthIcon: false,
          ...(logo ? { whiteLabelling: { loadingIconUrl: logo } } : {}),
        },
      },
    );

    pixelStreamingRef.current = PixelStreaming;
    setStatus('connecting');
    container.appendChild(Application.rootElement);

    const onVideoInitialized = () => {
      videoInitializedRef.current = true;
      // Drain the queue before React re-renders consumers so that a child
      // effect cannot emit `join_session` ahead of the queued `initial` packet.
      flushPreOpenMessages();
      setStatus('connected');
    };
    const onWebsocketClose = () => {
      videoInitializedRef.current = false;
      flushedRef.current = false;
      setStatus('disconnected');
    };
    const onError = () => {
      setStatus('error');
    };
    const onApplicationResponse = (response: string) => {
      try {
        const data = JSON.parse(response) as { type?: string };
        if (!data?.type) return;
        descriptor.emit(data.type, data);
        console.debug('emit applicationResponse', data.type, data);
      } catch {
        // Unreal can send non-JSON responses; only typed JSON messages are routed
      }
    };

    PixelStreaming.videoInitializedHandler.add(onVideoInitialized);
    PixelStreaming.websocketOnCloseHandler.add(onWebsocketClose);
    PixelStreaming.errorHandler.add(onError);
    PixelStreaming.applicationResponseHandler.add(onApplicationResponse);

    return () => {
      PixelStreaming.videoInitializedHandler.remove(onVideoInitialized);
      PixelStreaming.websocketOnCloseHandler.remove(onWebsocketClose);
      PixelStreaming.errorHandler.remove(onError);
      PixelStreaming.applicationResponseHandler.remove(onApplicationResponse);
      PixelStreaming.disconnect();
      if (Application.rootElement.parentElement === container) {
        container.removeChild(Application.rootElement);
      }
      videoInitializedRef.current = false;
      flushedRef.current = false;
      preOpenMessagesRef.current = [];
      pixelStreamingRef.current = null;
    };
  }, [shareId, logo, descriptor, flushPreOpenMessages]);

  const connect = useCallback(() => {
    pixelStreamingRef.current?.connect();
  }, []);

  const emitUIInteraction = useCallback((value: any) => {
    const pixelStreaming = pixelStreamingRef.current;
    // Queue until the video is initialized AND the pre-open queue has been
    // flushed. This keeps every message behind the `initial` packet until it
    // has been sent, preventing out-of-order delivery of `join_session`.
    if (!videoInitializedRef.current || !flushedRef.current || !pixelStreaming) {
      preOpenMessagesRef.current.push(value);
      return;
    }
    pixelStreaming.emitUIInteraction(value);
    console.debug('emit emitUIInteraction', value);
  }, []);

  return (
    <ArcwareContext.Provider value={{ status }}>
      <PixelStreamingContext.Provider
        value={{
          emitUIInteraction,
          descriptor,
          isStreaming: status === 'connected',
          status,
          connect,
        }}
      >
        <div className={styles.container}>
          {children}
          <div ref={containerRef} className={styles.pixelstream} />
        </div>
      </PixelStreamingContext.Provider>
    </ArcwareContext.Provider>
  );
}

export function useArcwareContext() {
  const context = useContext(ArcwareContext);
  if (!context) {
    throw new Error('useArcwareContext must be used within an ArcwareContextProvider');
  }
  return context;
}
